import { AdminExtAlert, DefaultApiGetAlertsRequest } from "@coeff/api-admin-ext";
import { useQuery } from "@tanstack/react-query";
import { Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { QueryCacheKey, SortColumn, SortDirection } from "../../../api";
import {
  AdminTableFooter,
  EmailIconCircle,
  FlexColumn,
  FlexRow,
  GoogleSheet,
  LoaderWithPerfTimings,
  SlackIconCircle,
  SortDropdown,
  Typography,
} from "../../../components";
import { AdminTable } from "../../../components/AdminTable";
import { StatusTag } from "../../../components/StatusTag";
import { getScheduleSummaryText, getTimeZone, useApiContext } from "../../../utils";
import { useTableFilters } from "../useTableFilters";

import { AlertDetailsDrawer } from "./AlertDetailsDrawer";
import { AlertsFilterToolbar } from "./AlertsFilterToolbar";
import { getSortIcon } from "./DataImports";

const Container = styled.div`
  height: 100%;
  width: 100%;

  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 12px;
  overflow: hidden;
  overflow-x: auto;
`;

export const Alerts: React.FC = () => {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const domain = searchParams.get("domain") ?? "all_domains";

  const { adminExtApiClient } = useApiContext();

  const [search, setSearch] = useState("");

  const [activeAlertId, setActiveAlertId] = React.useState<string>();

  const [page, setPage] = React.useState(0);

  const pageSize = 100;

  const [pageOffset, setPageOffset] = React.useState<number>(0);

  const setPageAndOffset = (page: number) => {
    setPage(page);

    setPageOffset(page * pageSize);
  };

  const { filters, setFilters } = useTableFilters<keyof DefaultApiGetAlertsRequest>([
    "status",
    "destination",
    "alert_type",
    "user_id",
  ]);

  const onFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    setPage(0);
    setPageOffset(0);
  };

  const [sortedColumn, setSortedColumn] = useState<SortColumn>({
    columnKey: "created_dt",
    value: SortDirection.ascend,
  });

  const { data: alertsResponse, isLoading: isLoadingAlerts } = useQuery(
    [QueryCacheKey.ADMIN_EXT_ALERTS, domain, pageSize, pageOffset, sortedColumn, search, filters],
    () =>
      adminExtApiClient.getAlerts({
        domain,
        page_size: pageSize,
        offset: pageOffset,
        sort_by: sortedColumn.columnKey,
        sort_order: sortedColumn.value,
        tz_name: getTimeZone(),
        search,
        status: filters.status as unknown as string,
        destination: filters.destination as unknown as string,
        alert_type: filters.alert_type as unknown as string,
        user_id: filters.user_id as unknown as string,
      }),
    {
      enabled: Boolean(domain),
    }
  );

  const alerts = alertsResponse?.data.alerts ?? [];

  const alertTypeLabels: Record<AdminExtAlert["alert_type"], string> = {
    monitor_change: "Monitor change",
    monitor_condition: "Monitor condition",
    new_rows_appended: "New rows appended",
    new_rows_with_id: "New rows with ID",
    snapshot: "Scheduled",
  };

  const columns: ColumnsType<AdminExtAlert> = [
    {
      title: <Typography noWrap>Alert</Typography>,
      dataIndex: "alert_name",
      key: "alert_name",
      showSorterTooltip: false,
      width: 300,
      filterDropdown: ({ confirm }) => (
        <SortDropdown confirm={confirm} columnKey="alert_name" setSortedColumn={setSortedColumn} />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "alert_name"),
      render: (alertName: AdminExtAlert["alert_name"], alert: AdminExtAlert) => (
        <FlexRow style={{ maxWidth: 300 }}>
          <GoogleSheet height="24px" width="24px" />

          <FlexColumn gap={0} minWidthZero>
            <Tooltip title={alertName}>
              <Typography noWrap fontWeight={500}>
                {alertName}
              </Typography>
            </Tooltip>

            <Typography
              title={alert.google_sheets_file_name}
              noWrap
              color="textSecondary"
              fontSize="13px"
              underline={Boolean(alert.spreadsheet_url)}
              onClick={e => {
                e.stopPropagation();

                alert.spreadsheet_url ? window.open(alert.spreadsheet_url, "_blank") : null;
              }}
            >
              {alert.google_sheets_file_name || "-"}
            </Typography>
          </FlexColumn>
        </FlexRow>
      ),
    },
    {
      title: <Typography noWrap>Last Sent</Typography>,
      dataIndex: "last_send_dt",
      key: "last_send_dt",
      showSorterTooltip: false,
      width: 200,
      filterDropdown: ({ confirm }) => (
        <SortDropdown
          confirm={confirm}
          columnKey="last_send_dt"
          setSortedColumn={setSortedColumn}
        />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "last_send_dt"),
      render: (lastSendDt: AdminExtAlert["last_send_dt"]) =>
        lastSendDt ? (
          <StatusTag
            status="ok"
            label={
              <Typography
                noWrap
                fontSize="13px"
                title={moment(lastSendDt).format("MMM D, YYYY h:mma")}
              >
                {moment(lastSendDt).fromNow()}
              </Typography>
            }
          />
        ) : (
          "-"
        ),
    },
    {
      title: <Typography noWrap>Destination</Typography>,
      dataIndex: "alert_type",
      key: "alert_type",
      showSorterTooltip: false,
      width: 200,
      render: (_, alert) => {
        const label =
          alert.recipients?.slack_enabled && alert.recipients.email_enabled
            ? "Slack & Email"
            : alert.recipients?.slack_enabled
            ? "Slack"
            : alert.recipients?.email_enabled
            ? "Email"
            : "-";

        return (
          <FlexRow
            gap={
              alert.recipients?.slack_enabled && alert.recipients?.email_enabled ? 3.5 : undefined
            }
          >
            <FlexRow style={{ position: "relative" }}>
              {alert.recipients?.email_enabled ? <EmailIconCircle /> : null}

              {alert.recipients?.slack_enabled ? (
                <SlackIconCircle
                  style={
                    alert.recipients.email_enabled ? { position: "absolute", left: 20 } : undefined
                  }
                />
              ) : null}
            </FlexRow>
            <div style={{ whiteSpace: "nowrap" }}>{label}</div>
          </FlexRow>
        );
      },
    },
    {
      title: <Typography noWrap>Trigger</Typography>,
      dataIndex: "alert_type",
      key: "alert_type",
      showSorterTooltip: false,
      width: 200,
      filterDropdown: ({ confirm }) => (
        <SortDropdown confirm={confirm} columnKey="alert_type" setSortedColumn={setSortedColumn} />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "alert_type"),
      render: (type: string, alert) => {
        return (
          <div>
            <div>{alertTypeLabels[type]}</div>
            {alert.scheduling_info ? (
              <Typography color="textSecondary" noWrap>
                {getScheduleSummaryText(alert.scheduling_info)}
              </Typography>
            ) : null}
          </div>
        );
      },
    },
    {
      title: <Typography noWrap>Created By</Typography>,
      dataIndex: "created_by",
      key: "created_by",
      showSorterTooltip: false,
      width: 200,
      filterDropdown: ({ confirm }) => (
        <SortDropdown confirm={confirm} columnKey="created_by" setSortedColumn={setSortedColumn} />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "created_by"),
      render: (createdBy: AdminExtAlert["created_by"], alert: AdminExtAlert) => {
        return (
          <div style={{ maxWidth: 200 }}>
            <Typography title={alert.created_by_email} noWrap>
              {createdBy}
            </Typography>

            {alert.created_dt ? (
              <Typography
                title={moment(alert.created_dt).format("MMM D, YYYY h:mma")}
                noWrap
                color="textSecondary"
                fontSize="13px"
              >
                {moment(alert.created_dt).fromNow()}
              </Typography>
            ) : null}
          </div>
        );
      },
    },
    {
      title: <Typography noWrap>Total Sends</Typography>,
      dataIndex: "total_sent",
      key: "total_sent",
      showSorterTooltip: false,
      width: 160,
      filterDropdown: ({ confirm }) => (
        <SortDropdown confirm={confirm} columnKey="total_sent" setSortedColumn={setSortedColumn} />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "total_sent"),
      render: (totalSent: AdminExtAlert["total_sent"]) =>
        totalSent === 0 ? (
          <Typography color="textSecondary">Never sent</Typography>
        ) : (
          totalSent.toLocaleString() ?? "-"
        ),
    },
  ];

  return (
    <Container>
      <AlertsFilterToolbar
        domain={domain}
        search={search}
        onSearchChange={value => setSearch(value)}
        filters={filters}
        setFilters={onFilterChange}
      />

      <FlexColumn gap={0.5} style={{ overflow: "hidden" }}>
        <AdminTable
          onRow={alert => ({
            onClick: () => {
              setActiveAlertId(alert.alert_id);
            },
          })}
          columns={columns}
          dataSource={alerts.map(alert => ({
            key: alert.alert_id ?? "",
            ...alert,
          }))}
          loading={false}
          locale={{
            emptyText: isLoadingAlerts ? (
              <LoaderWithPerfTimings name="DomainAlerts.locale" />
            ) : (
              "No alerts found"
            ),
          }}
          rowKey={r => r.alert_id ?? ""}
          pagination={false}
        />

        <AdminTableFooter
          page={page}
          pageSize={pageSize}
          onPageChange={page => setPageAndOffset(page)}
          data={alerts.map(x => [x.alert_name ?? "", true]) ?? []}
          totalRowCount={alertsResponse?.data.total_records ?? 0}
        />

        <AlertDetailsDrawer alertId={activeAlertId} onClose={() => setActiveAlertId(undefined)} />
      </FlexColumn>
    </Container>
  );
};
